import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import MicInputSelector from '../components/MicInputSelector';
import {
  Mic, MicOff, Play, Pause, Swords, Loader2, Share2, Trophy,
  RotateCcw, Volume2, Clock, ChevronRight, Copy, User
} from 'lucide-react';

const REACTIONS = [
  { emoji: '🔥', type: 'fire' },
  { emoji: '💎', type: 'diamond' },
  { emoji: '🚀', type: 'rocket' },
  { emoji: '😂', type: 'laugh' },
  { emoji: '⚡', type: 'bolt' },
];

function WaveformViz({ active = false, className = '' }) {
  return (
    <div className={`flex items-end gap-px h-10 ${className}`}>
      {Array.from({ length: 20 }).map((_, i) => (
        <div key={i} className={`flex-1 rounded-t-sm transition-all duration-100 ${
          active ? 'bg-violet-400' : 'bg-white/10'
        }`} style={{ height: active ? `${15 + Math.random() * 85}%` : '15%', transitionDelay: `${i * 15}ms` }} />
      ))}
    </div>
  );
}

function BattleSide({ user, isLeft, speaking, audioUrl, label = 'Player', reactionCount = 0 }) {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  const playAudio = () => {
    if (!audioUrl) return;
    if (playing) { audioRef.current?.pause(); setPlaying(false); return; }
    const audio = new Audio(audioUrl);
    audio.onended = () => setPlaying(false);
    audio.play();
    audioRef.current = audio;
    setPlaying(true);
  };

  return (
    <div className={`flex-1 flex flex-col items-center justify-center p-4 relative ${
      speaking ? (isLeft ? 'bg-violet-500/5' : 'bg-orange-500/5') : ''
    }`} data-testid={isLeft ? 'battle-side-left' : 'battle-side-right'}>
      {/* Speaking glow */}
      {speaking && (
        <div className={`absolute inset-0 border-2 rounded-xl animate-pulse ${
          isLeft ? 'border-violet-500/30' : 'border-orange-500/30'
        }`} />
      )}

      {/* Avatar */}
      <div className={`w-20 h-20 md:w-24 md:h-24 rounded-full border-2 flex items-center justify-center mb-3 ${
        speaking
          ? `${isLeft ? 'border-violet-400 shadow-lg shadow-violet-500/30' : 'border-orange-400 shadow-lg shadow-orange-500/30'} animate-pulse`
          : 'border-white/20'
      } bg-white/5`}>
        {user?.avatar_url ? (
          <img src={user.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
        ) : (
          <User className="w-10 h-10 text-white/30" />
        )}
      </div>

      <p className="font-bold text-sm mb-1">{user?.name || label}</p>
      {user?.handle && <p className="text-xs text-muted-foreground mb-3">@{user.handle}</p>}

      {/* Waveform */}
      <WaveformViz active={speaking || playing} className="w-full max-w-[200px] mb-3" />

      {/* Mic status */}
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
        {speaking ? (
          <><Mic className={`w-3.5 h-3.5 ${isLeft ? 'text-violet-400' : 'text-orange-400'}`} /> Speaking</>
        ) : (
          <><MicOff className="w-3.5 h-3.5" /> Idle</>
        )}
      </div>

      {/* Play recorded audio */}
      {audioUrl && (
        <Button onClick={playAudio} variant="outline" size="sm" className="border-white/10 text-xs">
          {playing ? <Pause className="w-3 h-3 mr-1" /> : <Play className="w-3 h-3 mr-1" />}
          {playing ? 'Stop' : 'Replay'}
        </Button>
      )}

      {/* Reaction count */}
      {reactionCount > 0 && (
        <div className="absolute top-3 right-3 bg-amber-500/20 text-amber-400 text-xs font-bold px-2 py-0.5 rounded-full">
          🔥 {reactionCount}
        </div>
      )}
    </div>
  );
}

export default function BattleRoomPage() {
  const { battleId } = useParams();
  const { user, isAuthenticated, loading: authLoading, getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  const [phase, setPhase] = useState('setup'); // setup, recording, waiting, opponent, ended
  const [round, setRound] = useState(1);
  const [maxRounds] = useState(3);
  const [recording, setRecording] = useState(false);
  const [timer, setTimer] = useState(0);
  const [myAudios, setMyAudios] = useState([]);
  const [botAudios, setBotAudios] = useState([]);
  const [botSpeaking, setBotSpeaking] = useState(false);
  const [micReady, setMicReady] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [reactions, setReactions] = useState({ left: 0, right: 0 });
  const [ended, setEnded] = useState(false);
  const [winner, setWinner] = useState(null);
  const [botResponding, setBotResponding] = useState(false);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  const handleMicReady = (deviceId) => {
    setSelectedDeviceId(deviceId);
    setMicReady(true);
    setPhase('recording');
  };

  const startRecording = async () => {
    try {
      const constraints = selectedDeviceId
        ? { audio: { deviceId: { exact: selectedDeviceId } } }
        : { audio: true };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        setMyAudios(prev => [...prev, { round, url, blob }]);
        handleAfterRecording(blob);
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setRecording(true);
      setTimer(0);
      timerRef.current = setInterval(() => setTimer(t => t + 1), 1000);
    } catch (e) {
      toast.error('Could not access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  };

  const handleAfterRecording = async (blob) => {
    setPhase('opponent');
    setBotResponding(true);

    // Send to BattleBot for AI response
    try {
      const headers = getAuthHeaders();

      // First transcribe
      const formData = new FormData();
      formData.append('audio', blob, 'recording.webm');
      formData.append('session_id', battleId || 'battle_room');

      const transcribeRes = await fetch(`${API}/battlebot/transcribe`, {
        method: 'POST', headers, body: formData,
      });

      let userText = '';
      if (transcribeRes.ok) {
        const td = await transcribeRes.json();
        userText = td.text || '';
      }

      // Get bot response
      const respRes = await fetch(`${API}/battlebot/respond`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: battleId || 'battle_room', user_text: userText, round }),
      });

      if (respRes.ok) {
        const rd = await respRes.json();
        // Get TTS audio
        const ttsRes = await fetch(`${API}/battlebot/tts`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: rd.response || rd.text || '', session_id: battleId, voice_id: 'pNInz6obpgDQGcFmaJgB' }),
        });

        if (ttsRes.ok) {
          const ttsData = await ttsRes.json();
          setBotAudios(prev => [...prev, { round, url: ttsData.audio_url, text: rd.response || rd.text }]);
          // Play bot response
          setBotSpeaking(true);
          const audio = new Audio(ttsData.audio_url);
          audio.onended = () => {
            setBotSpeaking(false);
            if (round < maxRounds) {
              setRound(r => r + 1);
              setPhase('recording');
            } else {
              endBattle();
            }
          };
          audio.play();
          audioRef.current = audio;
        }
      }
    } catch (e) {
      toast.error('Bot response failed');
      setPhase('recording');
    } finally {
      setBotResponding(false);
    }
  };

  const endBattle = () => {
    setPhase('ended');
    setEnded(true);
    // Determine winner based on reactions
    if (reactions.left > reactions.right) setWinner('player');
    else if (reactions.right > reactions.left) setWinner('bot');
    else setWinner('tie');
  };

  const sendReaction = (side) => {
    setReactions(prev => ({ ...prev, [side]: prev[side] + 1 }));
  };

  const shareBattle = () => {
    const url = window.location.href;
    navigator.clipboard?.writeText(url);
    toast.success('Battle link copied!');
  };

  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-violet-400" /></div>;
  }

  // Mic setup phase
  if (!micReady) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-zinc-950 via-purple-950/10 to-zinc-950 flex items-center justify-center p-4" data-testid="battle-room-setup">
        <div className="max-w-sm w-full space-y-4">
          <div className="text-center mb-6">
            <Swords className="w-12 h-12 text-violet-400 mx-auto mb-3" />
            <h1 className="text-2xl font-bold">Battle Room</h1>
            <p className="text-sm text-muted-foreground">Set up your mic before entering</p>
          </div>
          <MicInputSelector onReady={handleMicReady} onDeviceSelected={setSelectedDeviceId} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col" data-testid="battle-room">
      {/* Top Bar */}
      <div className="bg-black/60 border-b border-white/5 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Swords className="w-5 h-5 text-violet-400" />
          <span className="text-sm font-bold">Battle Room</span>
          <span className="text-xs bg-violet-500/20 text-violet-400 px-2 py-0.5 rounded-full">Round {round}/{maxRounds}</span>
        </div>
        <div className="flex items-center gap-2">
          {recording && (
            <span className="flex items-center gap-1 text-xs text-red-400 animate-pulse">
              <span className="w-2 h-2 rounded-full bg-red-500" /> REC {timer}s
            </span>
          )}
          <Button variant="ghost" size="sm" onClick={shareBattle} className="text-xs">
            <Share2 className="w-3.5 h-3.5 mr-1" /> Share
          </Button>
        </div>
      </div>

      {/* Split Screen */}
      <div className="flex-1 flex flex-col md:flex-row">
        {/* LEFT - Player */}
        <BattleSide
          user={user}
          isLeft={true}
          speaking={recording}
          audioUrl={myAudios[myAudios.length - 1]?.url}
          label="You"
          reactionCount={reactions.left}
        />

        {/* CENTER DIVIDER */}
        <div className="flex md:flex-col items-center justify-center py-3 md:py-0 md:px-3 relative">
          <div className="w-full md:w-px h-px md:h-full bg-gradient-to-r md:bg-gradient-to-b from-transparent via-white/20 to-transparent absolute" />
          <div className="relative z-10 bg-zinc-950 rounded-full p-2 border border-white/10">
            {phase === 'ended' ? (
              <Trophy className="w-6 h-6 text-amber-400" />
            ) : (
              <Swords className="w-6 h-6 text-violet-400" />
            )}
          </div>
          <div className="absolute bg-zinc-950 px-2 py-0.5 text-[10px] font-bold text-white/40" style={{ top: '60%' }}>
            {phase === 'recording' ? 'Your Turn' : phase === 'opponent' ? 'Opponent' : phase === 'ended' ? 'Finished' : 'VS'}
          </div>
        </div>

        {/* RIGHT - Opponent (Bot) */}
        <BattleSide
          user={{ name: 'AI Battle Bot', handle: 'battlebot' }}
          isLeft={false}
          speaking={botSpeaking}
          audioUrl={botAudios[botAudios.length - 1]?.url}
          label="AI Bot"
          reactionCount={reactions.right}
        />
      </div>

      {/* Bottom Controls */}
      <div className="bg-black/60 border-t border-white/5 p-4">
        {phase === 'ended' ? (
          <div className="text-center space-y-3" data-testid="battle-end">
            <p className="text-2xl font-black">
              {winner === 'player' ? '🏆 You Win!' : winner === 'bot' ? 'Bot Wins 🤖' : "It's a Tie! 🤝"}
            </p>
            <div className="flex items-center justify-center gap-6 text-sm">
              <span>You: 🔥 {reactions.left}</span>
              <span>Bot: 🔥 {reactions.right}</span>
            </div>
            <div className="flex justify-center gap-2">
              <Button onClick={() => { setRound(1); setPhase('recording'); setMyAudios([]); setBotAudios([]); setReactions({ left: 0, right: 0 }); setEnded(false); }}
                className="bg-violet-600 hover:bg-violet-700" data-testid="battle-rematch-btn">
                <RotateCcw className="w-4 h-4 mr-1" /> Rematch
              </Button>
              <Button variant="outline" onClick={shareBattle}>
                <Share2 className="w-4 h-4 mr-1" /> Share
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-4">
            {/* Record button */}
            {phase === 'recording' && !botResponding && (
              <button
                onClick={recording ? stopRecording : startRecording}
                className={`w-16 h-16 rounded-full flex items-center justify-center transition-all ${
                  recording
                    ? 'bg-red-500 hover:bg-red-600 animate-pulse shadow-lg shadow-red-500/40'
                    : 'bg-violet-500 hover:bg-violet-600 shadow-lg shadow-violet-500/30'
                }`}
                data-testid="battle-record-btn"
              >
                {recording ? <MicOff className="w-7 h-7 text-white" /> : <Mic className="w-7 h-7 text-white" />}
              </button>
            )}

            {/* Bot responding indicator */}
            {(phase === 'opponent' || botResponding) && (
              <div className="flex items-center gap-2 text-sm text-orange-400">
                <Loader2 className="w-5 h-5 animate-spin" />
                Bot Responding...
              </div>
            )}

            {/* Reactions */}
            <div className="flex items-center gap-1 ml-4">
              {REACTIONS.map(r => (
                <button key={r.type} onClick={() => sendReaction('left')}
                  className="text-xl hover:scale-125 active:scale-90 transition-transform" title={`React ${r.emoji}`}>
                  {r.emoji}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
